import { AdminRole } from './core/auth/models/admin-user-info';
import { CountryFeature, isFeatureAvailable } from './core/country/country-features';
import { CountryCode } from './core/country/country.model';

/**
 * One sidebar navigation entry. Items with `children` render as an expansion panel;
 * plain items render as a single link. Adding a feature to the sidebar means adding
 * one entry (or one child) to `APP_NAV` — the template loop does the rest.
 */
export interface AppNavItem {
    label: string;
    /** Material icon name; shown for top-level items (children render as plain links). */
    icon?: string;
    /** Route for plain links; omit for group items with children. */
    route?: string;
    children?: AppNavItem[];
    /** When set, the item is visible only to users with exactly this role. */
    requiredRole?: AdminRole;
    /** When set, the item is visible only to users whose role is one of these. */
    requiredRoles?: AdminRole[];
    /** When set, the item is visible only while the active country supports the feature (G8). */
    countryFeature?: CountryFeature;
    /**
     * When set, the item is hidden on the production deployment.
     *
     * Cosmetic only, and deliberately so: the route's own `stagingOnlyGuard` and the service's
     * `Editor:Enabled=false` are what keep production from being edited. This only stops offering staff
     * a link that would immediately bounce.
     */
    stagingOnly?: boolean;
    /**
     * When set, the item is shown only while the API environment is a per-MR dynamic env.
     *
     * Cosmetic in the same way as `stagingOnly`: the route's `dynEnvOnlyGuard` is what keeps the
     * page off staging and production. The page it gates runs environment bootstrap jobs (search
     * reindex, postal-code indexing), which only make sense against a throwaway backend.
     */
    dynEnvOnly?: boolean;
    /** `data-test-id` for E2E locators. */
    testId?: string;
}

export const APP_NAV: AppNavItem[] = [
    { label: 'Dashboard', icon: 'dashboard', route: '/dashboard' },
    {
        label: 'Users',
        icon: 'group',
        // The parent carries the UNION of its children's roles, which here is the Admin+Moderator gate
        // all of them already had — the two customer lists as top-level items, Customer Notes from the
        // Moderation group it moves out of. Notes are written about a person, so they belong beside the
        // people rather than in a queue of things awaiting a decision, and the same holds for the jobs
        // a worker says they have held.
        //
        // Employment Histories needs no `requiredRoles` of its own: legacy Backoffice split the gate
        // (Administrator + ContentManager for the global list, and +Moderator for the per-worker one),
        // but Admin renders ONE grid in both places, so it needs one gate. Moderator is the wider of
        // the two legacy gates and the one the per-worker surface already granted, so the group's
        // Admin+Moderator is exactly right — a narrower child gate would take away access the legacy
        // per-worker page gave.
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'Workers', route: '/customers/workers', testId: 'nav-customers-workers' },
            { label: 'Employers', route: '/customers/employers', testId: 'nav-customers-employers' },
            { label: 'Customer Notes', route: '/customer-notes', testId: 'nav-customer-notes' },
            { label: 'Employment Histories', route: '/employment-histories', testId: 'nav-employment-histories' },
        ],
    },
    // The two catalog moderation queues sit next to the customer lists they belong to, not under Moderation:
    // as plain items they carry the role gate the Moderation group used to give them.
    { label: 'Resumes', icon: 'description', route: '/resume-moderation', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-resume-moderation' },
    { label: 'Vacancies', icon: 'assignment', route: '/vacancy-moderation', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-vacancy-moderation' },
    {
        label: 'Ordering',
        icon: 'shopping_cart',
        children: [
            { label: 'Subscriptions', route: '/subscriptions', testId: 'nav-subscriptions' },
            { label: 'Payments', route: '/payments', testId: 'nav-payments' },
            { label: 'Tariffs', route: '/tariffs', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-tariffs' },
            { label: 'Bonus Transactions', route: '/bonuses', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-bonuses' },
            { label: 'Disputes', route: '/disputes', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-disputes' },
            { label: 'Regular Promos', route: '/promos/regular', testId: 'nav-promos-regular' },
            { label: 'Personal Promos', route: '/promos/personal', testId: 'nav-promos-personal' },
            { label: 'Mass Promo Configs', route: '/promos/mass-configs', testId: 'nav-promos-mass-configs' },
            { label: 'Event Promo Configs', route: '/promos/event-configs', testId: 'nav-promos-event-configs' },
            { label: 'Referral System', route: '/referrals', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-referrals' },
            // The payment-provider pages are Admin-only: the legacy Backoffice controllers they port
            // excluded Moderator, and these grids expose raw provider payloads and card metadata.
            // The YooKassa and Platron four are RU-only for the reason spelled out on the
            // `russianPaymentProviders` feature; Stripe settles everywhere, so its two are not gated.
            { label: 'YooKassa Event Logs', route: '/yookassa-event-logs', requiredRoles: [AdminRole.Admin], countryFeature: 'russianPaymentProviders', testId: 'nav-yookassa-event-logs' },
            { label: 'YooKassa Rejected Payments', route: '/yookassa-rejected-payments', requiredRoles: [AdminRole.Admin], countryFeature: 'russianPaymentProviders', testId: 'nav-yookassa-rejected-payments' },
            { label: 'Platron Logs', route: '/platron-logs', requiredRoles: [AdminRole.Admin], countryFeature: 'russianPaymentProviders', testId: 'nav-platron-logs' },
            { label: 'Platron Rejected Payments', route: '/platron-rejected-payments', requiredRoles: [AdminRole.Admin], countryFeature: 'russianPaymentProviders', testId: 'nav-platron-rejected-payments' },
            { label: 'Stripe Events', route: '/stripe-events', requiredRoles: [AdminRole.Admin], testId: 'nav-stripe-events' },
            { label: 'Stripe Rejected Payments', route: '/stripe-rejected-payments', requiredRoles: [AdminRole.Admin], testId: 'nav-stripe-rejected-payments' },
        ],
    },
    {
        label: 'Unmanaged Ordering',
        icon: 'sync_alt',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        countryFeature: 'unmanagedOrdering',
        children: [
            { label: 'Unmanaged Subscriptions', route: '/unmanaged-ordering/subscriptions', testId: 'nav-unmanaged-subscriptions' },
            { label: 'Unmanaged Payments', route: '/unmanaged-ordering/payments', testId: 'nav-unmanaged-payments' },
        ],
    },
    {
        label: 'Moderation',
        icon: 'verified_user',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'Background Checks', route: '/background-checks', countryFeature: 'backgroundChecks', testId: 'nav-background-checks' },
            { label: 'Phone Verifications', route: '/phone-verifications', testId: 'nav-phone-verifications' },
            // Sits beside its sibling: verifications are the SMS codes, logs are what changed the number.
            { label: 'Phone Number Logs', route: '/phone-number-logs', testId: 'nav-phone-number-logs' },
            // Not the same log as its neighbour: number logs are changes to a customer's own number,
            // access logs are other members asking to see it.
            { label: 'Phone Access Logs', route: '/phone-access-logs', testId: 'nav-phone-access-logs' },
            { label: 'Complaints', route: '/customer-complaints', testId: 'nav-customer-complaints' },
            { label: 'Honest reviews', route: '/honest-reviews', testId: 'nav-honest-reviews' },
            // Sits beside its namesake and is NOT the same thing: honest reviews are what members
            // write about each other, classic reviews are the support inbox behind the public Help /
            // Documents / Tax help / Rating forms.
            { label: 'Classic reviews', route: '/classic-reviews', testId: 'nav-classic-reviews' },
            // The third queue of text written about a member, and the only one written by an outsider:
            // a referee vouching for a worker they employed, which is why moderating one means editing
            // the referee's own name and contacts alongside the text.
            { label: 'Recommendations', route: '/recommendation-moderation', testId: 'nav-recommendation-moderation' },
            { label: 'Documents', route: '/documents', testId: 'nav-documents' },
            // Sits beside its sibling and is NOT the same queue: documents are the typed verification
            // records a moderator creates, user documents are what the customer uploaded from the Web
            // client, each a set of pages. Nothing turns one into the other, so both queues have to be worked.
            { label: 'User documents', route: '/user-documents', testId: 'nav-user-documents' },
            { label: 'Photos', route: '/photo-moderation', testId: 'nav-photo-moderation' },
            { label: 'Audio messages', route: '/audio-message-moderation', testId: 'nav-audio-message-moderation' },
            // A separate queue from Audio messages: those are the greeting on a listing, these are
            // the fairy tale a worker reads aloud for their profile.
            { label: 'Fairy tale audios', route: '/fairy-tale-audios', testId: 'nav-fairy-tale-audios' },
            { label: 'Blacklists', route: '/blacklists', testId: 'nav-blacklists' },
        ],
    },
    {
        label: 'Chat',
        icon: 'forum',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'Conversations', route: '/chats/conversations', testId: 'nav-chats' },
            { label: 'Chat Messages', route: '/chats/messages', testId: 'nav-chat-messages' },
            { label: 'Unread Users', route: '/chats/unread-users', testId: 'nav-chat-unread-users' },
        ],
    },
    {
        label: 'Notifications',
        icon: 'notifications',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            {
                label: 'Notification Configs',
                route: '/notifications/configs',
                testId: 'nav-notification-configs',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
            },
            {
                label: 'Mass Mailings',
                route: '/notifications/mailings',
                testId: 'nav-notification-mailings',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
            },
            {
                label: 'Sent Notifications',
                route: '/notifications/sent',
                testId: 'nav-notification-sent',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
            },
            { label: 'Delivery Logs', route: '/delivery-logs', testId: 'nav-delivery-logs' },
        ],
    },
    {
        label: 'SEO',
        icon: 'article',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'SEO Catalogs', route: '/seo/catalogs', testId: 'nav-seo-catalogs' },
            { label: 'SEO Selectors', route: '/seo/selectors', testId: 'nav-seo-selectors' },
        ],
    },
    {
        label: 'Localization',
        icon: 'translate',
        // The parent must carry the UNION of its children's roles: visibleNavItems runs the same
        // predicate on the group and each child, and a group that fails hides everything under it.
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator, AdminRole.Translator],
        children: [
            {
                label: 'Translations',
                route: '/localization/tags',
                testId: 'nav-localization-tags',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator, AdminRole.Translator],
                stagingOnly: true,
            },
            {
                // Reachable in production on purpose: exporting from staging and importing here is how
                // translations reach a production region at all.
                label: 'Import & export',
                route: '/localization/transfer',
                testId: 'nav-localization-transfer',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator, AdminRole.Translator],
            },
            // This child repeats the roles rather than inheriting them, and the group above is exactly why:
            // its parent admits Translator for the two pages above, so without its own gate this one would
            // be offered to a role its route guard bounces on arrival.
            {
                label: 'Dictionary order',
                route: '/localization/dictionaries',
                testId: 'nav-localization-dictionaries',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
            },
        ],
    },
    {
        label: 'AI Support',
        icon: 'support_agent',
        children: [
            { label: 'Support Cards', route: '/support-knowledge/cards', testId: 'nav-support-cards' },
            { label: 'AI Conversations', route: '/ai-conversations', testId: 'nav-ai-conversations' },
            { label: 'Analytics', route: '/ai-support-analytics', testId: 'nav-ai-support-analytics' },
        ],
    },
    {
        label: 'AI Search',
        icon: 'search',
        children: [
            { label: 'Requests', route: '/ai-search-requests' },
            { label: 'Analytics', route: '/ai-search-analytics' },
        ],
    },
    {
        label: 'Reports',
        icon: 'insights',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'Employer statistics', route: '/reports/employer-stats', testId: 'nav-reports-employer-stats' },
            { label: 'Employer payments', route: '/reports/employer-payments', testId: 'nav-reports-employer-payments' },
            { label: 'Worker statistics', route: '/reports/worker-stats', testId: 'nav-reports-worker-stats' },
            { label: 'Worker payments', route: '/reports/worker-payments', testId: 'nav-reports-worker-payments' },
            { label: 'Registrations', route: '/reports/registrations', testId: 'nav-reports-registrations' },
            { label: 'Conversations statistics', route: '/reports/chat-stats', testId: 'nav-reports-chat-stats' },
            { label: 'Chat abuse', route: '/reports/chat-abuse', testId: 'nav-reports-chat-abuse' },
            { label: 'Contact limit exceeded', route: '/reports/contact-limit', testId: 'nav-reports-contact-limit' },
            { label: 'Non-home country IP', route: '/reports/non-home-ip', testId: 'nav-reports-non-home-ip' },
            {
                label: 'Phone verification stats',
                route: '/reports/phone-verification-stats',
                testId: 'nav-reports-phone-verification-stats',
            },
            {
                label: 'Call Follow-Ups',
                route: '/reports/voice-reminder-stats',
                countryFeature: 'voiceReminders',
                testId: 'nav-reports-voice-reminder-stats',
            },
            {
                label: 'AI document checks',
                route: '/reports/ai-document-checks',
                testId: 'nav-reports-ai-document-checks',
            },
            { label: 'Sex offenders', route: '/reports/sex-offenders', testId: 'nav-reports-sex-offenders' },
            { label: 'Identifying agencies', route: '/reports/agency-detection', testId: 'nav-reports-agency-detection' },
        ],
    },
    {
        label: 'Geo',
        icon: 'travel_explore',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'Geo Units', route: '/geo/units', testId: 'nav-geo-units' },
            { label: 'Geo Lookup', route: '/geo/lookup', testId: 'nav-geo-lookup' },
        ],
    },
    {
        label: 'Schedulers',
        icon: 'schedule',
        requiredRole: AdminRole.Admin,
        children: [
            { label: 'Jobs', route: '/scheduler/jobs', testId: 'nav-scheduler-jobs' },
            { label: 'Executing', route: '/scheduler/executing', testId: 'nav-scheduler-executing' },
            { label: 'Analytics', route: '/scheduler/analytics', testId: 'nav-scheduler-analytics' },
        ],
    },
    {
        label: 'Other',
        icon: 'monitoring',
        // The group stays Admin-gated so Admins keep Settings; only the staff-account page below is
        // narrowed to SuperAdmin. A SuperAdmin passes the group gate through the superset rule.
        requiredRole: AdminRole.Admin,
        children: [
            { label: 'Users & Roles', route: '/users', requiredRole: AdminRole.SuperAdmin, testId: 'nav-users' },
            { label: 'Settings', route: '/settings', testId: 'nav-settings' },
            { label: 'Environment tools', route: '/environment-tools', dynEnvOnly: true, testId: 'nav-environment-tools' },
        ],
    },
];

/**
 * Role-, country- and deployment-filters the nav model (pure — consumed by the shell store's
 * `visibleNav` computed, which re-evaluates when the active country switches). Groups whose children
 * are all filtered out disappear entirely.
 *
 * `isProdDeployment` and `isDynamicEnv` are required rather than defaulted: a default would decide,
 * silently and in favour of showing more, what every future caller that forgets it gets.
 */
export function visibleNavItems(
    role: AdminRole | undefined,
    country: CountryCode,
    isProdDeployment: boolean,
    isDynamicEnv: boolean,
): AppNavItem[] {
    const isVisible = (item: AppNavItem): boolean => {
        // SuperAdmin is a superset of every other role, applied once here and once in
        // `role.guard.ts` rather than being added to every `requiredRole(s)` entry below — see the
        // note on the guard. These two are the route/nav gates; in-page capability gates name the
        // role themselves. Country and deployment gates still apply: they are about where a page
        // makes sense, not about who may open it.
        if (role === AdminRole.SuperAdmin) {
            if (item.countryFeature && !isFeatureAvailable(item.countryFeature, country)) return false;
            if (item.stagingOnly && isProdDeployment) return false;
            if (item.dynEnvOnly && !isDynamicEnv) return false;
            return true;
        }

        if (item.requiredRole && item.requiredRole !== role) return false;
        if (item.requiredRoles && !(role !== undefined && item.requiredRoles.includes(role))) return false;
        if (item.countryFeature && !isFeatureAvailable(item.countryFeature, country)) return false;
        if (item.stagingOnly && isProdDeployment) return false;
        if (item.dynEnvOnly && !isDynamicEnv) return false;
        return true;
    };
    return APP_NAV
        .filter(isVisible)
        .map(item => item.children ? { ...item, children: item.children.filter(isVisible) } : item)
        .filter(item => !item.children || item.children.length > 0);
}

export function navGroupLabelForUrl(nav: AppNavItem[], url: string): string | null {
    const path = navPath(url);
    for (const item of nav) {
        if (item.children?.some(child => child.route != null && isNavRouteActive(path, child.route))) {
            return item.label;
        }
    }
    return null;
}

function navPath(url: string): string {
    return url.split(/[?#]/, 1)[0];
}

function isNavRouteActive(path: string, route: string): boolean {
    return path === route || path.startsWith(`${route}/`);
}
